<template>
  <q-page class="results-page">
    <div class="results-container">
      <!-- Loading state -->
      <div v-if="loading" class="results-container">
        <q-skeleton type="text" width="180px" dark style="font-size: 1.5rem;" />
        <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-top: 24px;">
          <q-skeleton v-for="n in 3" :key="n" type="rect" height="80px" dark style="border-radius: 16px;" />
        </div>
        <q-skeleton type="rect" height="300px" dark style="border-radius: 16px; margin-top: 24px;" />
      </div>

      <!-- Not found -->
      <div v-else-if="!cycle || !cycle.finalScore" class="results-empty">
        <q-icon name="warning" size="48px" :style="{ color: 'var(--oa-text-disabled)' }" />
        <p class="empty-text">Evaluacion no encontrada o no completada.</p>
        <router-link to="/evaluations/instruments" class="empty-link">
          Volver a instrumentos
        </router-link>
      </div>

      <!-- Loaded state -->
      <template v-else>
        <!-- Header -->
        <div class="results-header">
          <router-link to="/evaluations" class="back-link">
            <q-icon name="arrow_back" size="16px" />
            Evaluaciones
          </router-link>
          <h1 class="results-title">Resultado de Evaluacion</h1>
          <div class="results-meta">
            <span class="meta-item">
              <q-icon name="fact_check" size="16px" />
              {{ INSTRUMENT_LABELS[cycle.instrument] || cycle.instrument }}
            </span>
            <span v-if="cycle.coach" class="meta-item">
              <q-icon name="person" size="16px" />
              {{ cycle.coach.firstName }} {{ cycle.coach.lastName }}
            </span>
            <span v-if="cycle.completedAt" class="meta-item">
              <q-icon name="event" size="16px" />
              {{ formatDate(cycle.completedAt) }}
            </span>
          </div>
        </div>

        <!-- Big score -->
        <div class="score-hero" :class="cnapConfig.bgClass">
          <p class="score-hero-value">{{ finalScore.toFixed(1) }}</p>
          <p class="score-hero-max">de 10</p>
          <q-badge
            :label="cnapConfig.label"
            :style="{ background: cnapConfig.badgeBg, color: cnapConfig.badgeColor, fontSize: '12px', padding: '4px 12px', borderRadius: '8px' }"
          />
        </div>

        <!-- Stats row -->
        <div class="stats-row">
          <div class="stat-card">
            <q-icon name="emoji_events" size="20px" :style="{ color: 'var(--oa-gold-full)' }" />
            <div>
              <p class="stat-value">{{ finalScore.toFixed(1) }}</p>
              <p class="stat-label">Score Final</p>
            </div>
          </div>
          <div class="stat-card">
            <q-icon name="check_circle" size="20px" :style="{ color: 'var(--oa-success)' }" />
            <div>
              <p class="stat-value">{{ strengths.length }}</p>
              <p class="stat-label">Fortalezas</p>
            </div>
          </div>
          <div class="stat-card">
            <q-icon name="flag" size="20px" :style="{ color: 'var(--oa-gold-full)' }" />
            <div>
              <p class="stat-value">{{ weaknesses.length }}</p>
              <p class="stat-label">A mejorar</p>
            </div>
          </div>
        </div>

        <!-- Radar chart -->
        <div v-if="radarData.length > 0" class="radar-section">
          <h2 class="section-title">Perfil por area</h2>
          <p class="section-subtitle">
            {{ hasPrevious ? 'Evaluacion actual vs anterior (linea punteada)' : 'Evaluacion actual' }}
          </p>
          <RadarChart :data="radarData" :show-previous="hasPrevious" :height="320" />
        </div>

        <!-- Area breakdown -->
        <div v-if="areaEntries.length > 0" class="area-breakdown">
          <h2 class="section-title-lg">Detalle por area</h2>
          <div class="area-list">
            <div
              v-for="(entry, i) in areaEntries"
              :key="entry.areaId"
              class="area-card"
            >
              <div class="area-card-top">
                <span class="area-name">Area {{ i + 1 }}</span>
                <div class="area-score-group">
                  <span class="area-score-text">{{ entry.data.normalizedScore.toFixed(1) }}/10</span>
                  <q-badge
                    :label="getAreaCnap(entry.data.normalizedScore).label"
                    :style="{
                      background: getAreaCnap(entry.data.normalizedScore).badgeBg,
                      color: getAreaCnap(entry.data.normalizedScore).badgeColor,
                      fontSize: '11px',
                      padding: '2px 8px',
                      borderRadius: '6px',
                    }"
                  />
                </div>
              </div>
              <q-linear-progress
                :value="entry.data.normalizedScore / 10"
                :color="getProgressColor(entry.data.normalizedScore)"
                track-color="grey-9"
                rounded
                size="6px"
                style="margin-top: 8px;"
              />
            </div>
          </div>
        </div>

        <!-- Strengths & Weaknesses -->
        <div class="sw-grid">
          <div v-if="strengths.length > 0" class="sw-card sw-strengths">
            <h3 class="sw-title sw-title-green">
              <q-icon name="check_circle" size="20px" />
              Fortalezas
            </h3>
            <ul class="sw-list">
              <li v-for="(s, i) in strengths" :key="i" class="sw-item">
                <span class="sw-marker sw-marker-green">+</span>
                {{ s }}
              </li>
            </ul>
          </div>

          <div v-if="weaknesses.length > 0" class="sw-card sw-weaknesses">
            <h3 class="sw-title sw-title-gold">
              <q-icon name="warning" size="20px" />
              Areas a mejorar
            </h3>
            <ul class="sw-list">
              <li v-for="(w, i) in weaknesses" :key="i" class="sw-item">
                <span class="sw-marker sw-marker-gold">!</span>
                {{ w }}
              </li>
            </ul>
          </div>
        </div>

        <!-- Action Plans -->
        <div v-if="actionPlan" class="action-plan-card">
          <h2 class="action-plan-title">
            <q-icon name="trending_up" size="20px" :style="{ color: 'var(--oa-gold-full)' }" />
            Plan de accion generado
          </h2>
          <p v-if="actionPlan.summary" class="action-plan-summary">{{ actionPlan.summary }}</p>
          <div class="action-plan-items">
            <div
              v-for="item in actionPlan.items"
              :key="item.id"
              class="action-plan-item"
            >
              <div
                class="priority-dot"
                :class="{
                  'dot-alta': item.priority === 'alta',
                  'dot-media': item.priority === 'media',
                  'dot-baja': item.priority === 'baja',
                }"
              />
              <div class="action-plan-item-content">
                <p class="action-plan-item-text">{{ item.action }}</p>
                <p class="action-plan-item-meta">{{ item.area }} — Prioridad {{ item.priority }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Feedback -->
        <div v-if="cycle.feedback" class="feedback-card">
          <h3 class="feedback-title">Feedback del evaluador</h3>
          <p class="feedback-text">{{ cycle.feedback }}</p>
        </div>
      </template>
    </div>
  </q-page>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { api } from 'boot/axios';
import RadarChart from 'components/charts/RadarChart.vue';

// ---------- Types ----------
interface AreaScore {
  rawScore: number;
  maxScore: number;
  normalizedScore: number;
  result: string;
}

interface ActionPlanItem {
  id: string;
  action: string;
  area: string;
  priority: 'alta' | 'media' | 'baja';
  completed: boolean;
}

interface ActionPlan {
  id: string;
  summary?: string;
  items: ActionPlanItem[];
}

interface CycleData {
  id: string;
  instrument: string;
  finalScore: string | number | null;
  completedAt: string | null;
  feedback: string | null;
  coach?: { firstName: string; lastName: string; role: string } | null;
  evaluator?: { firstName: string; lastName: string } | null;
  areaScores: Record<string, AreaScore> | null;
  previousAreaScores?: Record<string, { normalizedScore: number }> | null;
  strengths: string[] | null;
  weaknesses: string[] | null;
  actionPlans?: ActionPlan[];
}

interface CnapConfig {
  label: string;
  color: string;
  bgClass: string;
  badgeBg: string;
  badgeColor: string;
}

// ---------- Constants ----------
const INSTRUMENT_LABELS: Record<string, string> = {
  live_observation: 'Observacion en Vivo',
  performance_binary: 'Desempeno Operativo',
  protocol_checklist: 'Adherencia a Protocolos',
  routine_execution: 'Ejecucion de Rutinas',
};

const CNAP_MAP: Record<string, CnapConfig> = {
  C: { label: 'Critico', color: 'text-red-500', bgClass: 'score-hero-C', badgeBg: 'rgba(239,68,68,0.15)', badgeColor: '#ef4444' },
  N: { label: 'Necesita Mejora', color: 'text-orange-500', bgClass: 'score-hero-N', badgeBg: 'rgba(251,146,60,0.15)', badgeColor: '#fb923c' },
  A: { label: 'Adecuado', color: 'text-emerald-500', bgClass: 'score-hero-A', badgeBg: 'rgba(52,211,153,0.15)', badgeColor: '#34d399' },
  P: { label: 'Positivo', color: 'text-gold', bgClass: 'score-hero-P', badgeBg: 'rgba(201,169,110,0.15)', badgeColor: 'var(--oa-gold-full)' },
};

function getCNAP(score: number): string {
  if (score >= 9) return 'P';
  if (score >= 7) return 'A';
  if (score >= 5) return 'N';
  return 'C';
}

function getAreaCnap(score: number): CnapConfig {
  return CNAP_MAP[getCNAP(score)];
}

function getProgressColor(score: number): string {
  if (score >= 9) return 'amber';
  if (score >= 7) return 'green';
  if (score >= 5) return 'orange';
  return 'red';
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' });
}

// ---------- Router ----------
const route = useRoute();
const cycleIdParam = computed(() => route.params.cycleId as string);

// ---------- State ----------
const loading = ref(true);
const cycle = ref<CycleData | null>(null);

// ---------- Computed ----------
const finalScore = computed(() => parseFloat(String(cycle.value?.finalScore ?? 0)));
const cnap = computed(() => getCNAP(finalScore.value));
const cnapConfig = computed(() => CNAP_MAP[cnap.value]);

const areaEntries = computed(() => {
  if (!cycle.value?.areaScores) return [];
  return Object.entries(cycle.value.areaScores).map(([areaId, data]) => ({ areaId, data }));
});

const radarData = computed(() => {
  if (!cycle.value?.areaScores) return [];
  const prevScores = cycle.value.previousAreaScores
    ? Object.values(cycle.value.previousAreaScores)
    : null;

  return Object.entries(cycle.value.areaScores).map(([_, data], i) => ({
    subject: `Area ${i + 1}`,
    value: data.normalizedScore,
    fullMark: 10,
    previous: prevScores?.[i]?.normalizedScore,
  }));
});

const hasPrevious = computed(() => !!cycle.value?.previousAreaScores);

const strengths = computed(() => (cycle.value?.strengths as string[]) ?? []);
const weaknesses = computed(() => (cycle.value?.weaknesses as string[]) ?? []);
const actionPlan = computed(() => cycle.value?.actionPlans?.[0] || null);

// ---------- Data loading ----------
async function fetchCycle() {
  loading.value = true;
  try {
    const { data } = await api.get(`/supervision/cycles/${cycleIdParam.value}`);
    cycle.value = data;
  } catch (err) {
    console.error(err);
    cycle.value = null;
  } finally {
    loading.value = false;
  }
}

onMounted(() => {
  fetchCycle();
});
</script>

<style scoped lang="scss">
.results-page {
  padding: 24px;
}

.results-container {
  max-width: 900px;
  margin: 0 auto;
}

// === Empty ===
.results-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 80px 32px;
  text-align: center;
}

.empty-text {
  color: var(--oa-text-secondary);
  margin: 12px 0;
}

.empty-link {
  color: var(--oa-gold-full);
  font-size: var(--oa-text-sm);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

// === Header ===
.results-header {
  margin-bottom: 24px;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: var(--oa-text-sm);
  color: var(--oa-text-disabled);
  text-decoration: none;
  margin-bottom: 16px;
  transition: color 0.15s ease;

  &:hover {
    color: var(--oa-gold-full);
  }
}

.results-title {
  font-size: var(--oa-text-xl);
  font-weight: 700;
  color: var(--oa-text-primary);
  margin: 0;
}

.results-meta {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 8px;
  flex-wrap: wrap;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: var(--oa-text-sm);
  color: var(--oa-text-muted);
}

// === Score hero ===
.score-hero {
  border-radius: 16px;
  border: 1px solid var(--oa-border);
  padding: 32px;
  text-align: center;
  margin-bottom: 20px;
}

.score-hero-C {
  background: color-mix(in srgb, #ef4444 6%, var(--oa-bg-2));
  border-color: color-mix(in srgb, #ef4444 15%, transparent);
}

.score-hero-N {
  background: color-mix(in srgb, #fb923c 6%, var(--oa-bg-2));
  border-color: color-mix(in srgb, #fb923c 15%, transparent);
}

.score-hero-A {
  background: color-mix(in srgb, #34d399 6%, var(--oa-bg-2));
  border-color: color-mix(in srgb, #34d399 15%, transparent);
}

.score-hero-P {
  background: color-mix(in srgb, var(--oa-gold-full) 6%, var(--oa-bg-2));
  border-color: color-mix(in srgb, var(--oa-gold-full) 15%, transparent);
}

.score-hero-value {
  font-size: 3.5rem;
  font-weight: 700;
  color: var(--oa-text-primary);
  margin: 0;
}

.score-hero-max {
  font-size: var(--oa-text-sm);
  color: var(--oa-text-disabled);
  margin: 0 0 12px 0;
}

// === Stats row ===
.stats-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 24px;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
  border-radius: 16px;
  padding: 16px;
}

.stat-value {
  font-size: var(--oa-text-lg);
  font-weight: 700;
  color: var(--oa-text-primary);
  margin: 0;
}

.stat-label {
  font-size: var(--oa-text-xs);
  color: var(--oa-text-muted);
  margin: 2px 0 0 0;
}

// === Radar ===
.radar-section {
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
}

.section-title {
  font-size: var(--oa-text-md);
  font-weight: 600;
  color: var(--oa-text-primary);
  margin: 0 0 4px 0;
}

.section-subtitle {
  font-size: var(--oa-text-xs);
  color: var(--oa-text-disabled);
  margin: 0 0 16px 0;
}

// === Area breakdown ===
.area-breakdown {
  margin-bottom: 24px;
}

.section-title-lg {
  font-size: var(--oa-text-lg);
  font-weight: 600;
  color: var(--oa-text-primary);
  margin: 0 0 12px 0;
}

.area-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.area-card {
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
  border-radius: 16px;
  padding: 20px;
}

.area-card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.area-name {
  font-size: var(--oa-text-sm);
  font-weight: 500;
  color: var(--oa-text-primary);
}

.area-score-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.area-score-text {
  font-size: var(--oa-text-sm);
  font-weight: 700;
  color: var(--oa-text-primary);
}

// === Strengths / Weaknesses ===
.sw-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.sw-card {
  border-radius: 16px;
  padding: 20px;
}

.sw-strengths {
  background: color-mix(in srgb, var(--oa-success) 6%, transparent);
  border: 1px solid color-mix(in srgb, var(--oa-success) 15%, transparent);
}

.sw-weaknesses {
  background: color-mix(in srgb, var(--oa-gold-full) 6%, transparent);
  border: 1px solid color-mix(in srgb, var(--oa-gold-full) 15%, transparent);
}

.sw-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  margin: 0 0 12px 0;
}

.sw-title-green {
  color: var(--oa-success);
}

.sw-title-gold {
  color: var(--oa-gold-full);
}

.sw-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.sw-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: var(--oa-text-sm);
  color: var(--oa-text-secondary);
}

.sw-marker {
  margin-top: 2px;
  flex-shrink: 0;
  font-weight: 700;
}

.sw-marker-green {
  color: var(--oa-success);
}

.sw-marker-gold {
  color: var(--oa-gold-full);
}

// === Action plan ===
.action-plan-card {
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
}

.action-plan-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: var(--oa-text-primary);
  margin: 0 0 16px 0;
}

.action-plan-summary {
  font-size: var(--oa-text-sm);
  color: var(--oa-text-muted);
  margin: 0 0 16px 0;
}

.action-plan-items {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.action-plan-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid color-mix(in srgb, var(--oa-border) 30%, transparent);

  &:last-child {
    border-bottom: none;
  }
}

.priority-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-top: 6px;
  flex-shrink: 0;
}

.dot-alta {
  background: #ef4444;
}

.dot-media {
  background: #fbbf24;
}

.dot-baja {
  background: var(--oa-success);
}

.action-plan-item-content {
  flex: 1;
  min-width: 0;
}

.action-plan-item-text {
  font-size: var(--oa-text-sm);
  color: var(--oa-text-primary);
  margin: 0;
}

.action-plan-item-meta {
  font-size: var(--oa-text-xs);
  color: var(--oa-text-disabled);
  margin: 2px 0 0 0;
}

// === Feedback ===
.feedback-card {
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
  border-radius: 16px;
  padding: 24px;
}

.feedback-title {
  font-weight: 600;
  color: var(--oa-text-primary);
  margin: 0 0 8px 0;
}

.feedback-text {
  font-size: var(--oa-text-sm);
  color: var(--oa-text-secondary);
  white-space: pre-line;
  margin: 0;
}
</style>
